import { Theme } from '@nivo/core';
import { ResponsiveLine } from '@nivo/line';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import * as S from './styles';

const style: Theme = {
  axis: {
    ticks: { text: { fill: '#0C5C75', font: '"Montserrat", sans-serif', fontSize: '0.6em' } },
  },
  grid: { line: { stroke: '#d1d1d1', strokeWidth: 1, strokeDasharray: '3 3' } },
  tooltip: { container: { fontSize: '0.7em' } },
  legends: { text: { fontSize: '0.8em' } },
  labels: { text: { fontSize: '2em' } },
};

type GraphProps = {
    id: string,
    axis: {
        bottomLegend: string,
        leftLegend: string
    }
    data: {id: string, data: {x: string, y: number}[]}[],
    yMin?: number | "auto",
    yMax?: number | "auto",
    yStep?: number,
    showLiveLegend?: boolean,
    unitName?: string,
    sensorName?: string
};



export const LineGraph: React.FC<GraphProps> = ({ axis, data, yMin ='auto', yMax = 'auto', yStep, showLiveLegend = false, unitName, sensorName }) => {
  const gapMultiplier = 50;
  const minStep = 10;
  
  // Drag functionality state
  const [legendPosition, setLegendPosition] = useState({ top: 25, left: 50 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const legendRef = useRef<HTMLDivElement>(null);
  
  // Drag event handlers
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    setIsDragging(true);
    setDragStart({
      x: e.clientX - legendPosition.left,
      y: e.clientY - legendPosition.top,
    });
  }, [legendPosition]);

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!isDragging) return;
    
    const newLeft = e.clientX - dragStart.x;
    const newTop = e.clientY - dragStart.y;
    
    // Constrain to viewport bounds
    const maxLeft = window.innerWidth - 160; // legend width
    const maxTop = window.innerHeight - 100; // estimated legend height
    
    setLegendPosition({
      left: Math.max(0, Math.min(newLeft, maxLeft)),
      top: Math.max(0, Math.min(newTop, maxTop))
    });
  }, [isDragging, dragStart]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);
  
  const parseBrazilianDate = (dateStr: string): Date => {
    const [datePart, ...timeParts] = dateStr.split(' ');
    const timePart = timeParts.length > 0 ? ' ' + timeParts.join(' ') : '';
    
    if (datePart.includes('/')) {
      const parts = datePart.split('/');
      if (parts.length === 3) {
        const [day, month, year] = parts;
        return new Date(`${month}/${day}/${year}${timePart}`);
      }
    }
    
    return new Date(dateStr);
  };
    
    const dataWithGap = data.map(series => {
      let cumulativeX = 0;
      return {
        ...series,
        data: series.data.map((point, index) => {
          let step = minStep;
          
          if (index > 0) {
            const currentDate = parseBrazilianDate(point.x);
            const previousDate = parseBrazilianDate(series.data[index - 1].x);
            
            if (isNaN(currentDate.getTime())) {
              console.error('Invalid current date:', point.x, 'Type:', typeof point.x);
              step = minStep;
            } else if (isNaN(previousDate.getTime())) {
              console.error('Invalid previous date:', series.data[index - 1].x, 'Type:', typeof series.data[index - 1].x);
              step = minStep;
            } else {
              const timeDiffMs = currentDate.getTime() - previousDate.getTime();
              const timeDiffHours = timeDiffMs / (1000 * 60 * 60);
              
              if (isNaN(timeDiffHours) || timeDiffHours < 0) {
                console.error('Invalid time difference between:', series.data[index - 1].x, 'and', point.x);
                step = minStep;
              } else {
                step = Math.max(timeDiffHours * gapMultiplier, minStep);
              }
            }
          }
          
          if (isNaN(step) || !isFinite(step)) {
            step = minStep;
          }
          
          cumulativeX += step;
          
          if (isNaN(cumulativeX) || !isFinite(cumulativeX)) {
            cumulativeX = (index + 1) * minStep;
          }
          
          return {
            ...point,
            x: cumulativeX,
            dateLabel: point.x
          };
        })
      };
    });

  let legends: any = [];
    
    const allX = dataWithGap.flatMap(series => series.data.map(point => point.x));
    const maxX = Math.max(...allX);
    const minX = Math.min(...allX);
    const leftBuffer = minX * 5; // Left space buffer
    const rightBuffer = maxX * 0.05; // Right space buffer
    
    // Cores padrão do gráfico
    const defaultColors = ['#FFB30D', '#00CFEF', '#0C5C75', '#e3eff4'];
    
    // Função para obter o último valor de cada série
    const getLastValues = () => {
      return data.map((series, index) => {
        const lastPoint = series.data[series.data.length - 1];
        return {
          seriesId: series.id,
          sensorName: sensorName || series.id || 'Sensor',
          lastValue: lastPoint ? lastPoint.y : 'N/A',
          color: defaultColors[index % defaultColors.length]
        };
      }).filter(item => item.seriesId !== '' || sensorName); // Show if there's sensor name or series ID
    };
    let tickValues: number[] | undefined;
    if (yStep && typeof yMin === 'number' && typeof yMax === 'number' && yStep > 0) {
      const start = yMin;
      const end = yMax;
      tickValues = [];
      for (let v = start; v <= end; v += yStep) {
        tickValues.push(Number(v.toFixed(8)));
      }
      if (tickValues[tickValues.length - 1] !== end) tickValues.push(end);
    }
    const lastValues = getLastValues();
    
    return (
        <S.GraphContainer>
          {showLiveLegend && lastValues.length > 0 && (
            <S.LiveLegendContainer
              ref={legendRef}
              $top={legendPosition.top}
              $left={legendPosition.left}
              $isDragging={isDragging}
              onMouseDown={handleMouseDown}
            >
              <S.LegendTitle>Ultimo valor:</S.LegendTitle>
              {lastValues.map((item, index) => (
                <S.LegendItem key={index}>
                  <S.ColorIndicator color={item.color} />
                  <S.LegendText>
                    <S.LastValue>
                      {typeof item.lastValue === 'number' 
                        ? `${item.lastValue % 1 === 0 ? item.lastValue.toString() : item.lastValue.toFixed(2)} ${unitName || ''}` 
                        : item.lastValue}
                    </S.LastValue>
                  </S.LegendText>
                </S.LegendItem>
              ))}
            </S.LiveLegendContainer>
          )}
          <ResponsiveLine
              data={dataWithGap}
              margin={{ top: 25, right: 50, bottom: 150, left: 70 }}
              axisTop={null}
              axisRight={null}
              curve="linear"
              lineWidth={2}
              theme={style}
              xScale={{ type: 'linear', min: minX - leftBuffer, max: maxX + rightBuffer }}
              yScale={{
                type: 'linear',
                min: yMin,
                max: yMax,
                stacked: false,
                reverse: false,
              }}
              axisBottom={{
                orient: 'bottom',
                tickSize: 0,
                tickPadding: 30,
                legend: axis.bottomLegend,
                legendOffset: 100,
                legendPosition: 'middle',
                tickRotation: 75,
                tickValues: dataWithGap.flatMap(series => series.data.map(point => point.x)),
                format: value => {
                  const allPoints = dataWithGap.flatMap(series => series.data);
                  const found = allPoints.find(p => p.x === value);
                  if (found) {
                    const date = parseBrazilianDate(found.dateLabel);
                    return isNaN(date.getTime()) ? 'Invalid Date' : date.toLocaleString('pt-BR');
                  }
                  return String(value);
                }
              }}
              axisLeft={{
                orient: 'left',
                tickPadding: 12,
                legendOffset: -50, //left unit legend
                tickSize: 0,
                legendPosition: 'middle',
                legend: axis.leftLegend,
                tickValues
              }}
              pointSize={10}
              //pointColor={{ theme: 'background' }}
              // pointBorderWidth={1}
              // pointBorderColor={{ from: 'seriesColor' }}
              // pointLabelYOffset={-12}
              useMesh={true}
              tooltip={({ point }) => {
                const allPoints = dataWithGap.flatMap(series => series.data);
                const found = allPoints.find(p => p.x === point.data.x);
                const dateToShow = found ? found.dateLabel : 'N/A';
                
                let formattedDate = 'N/A';
                if (dateToShow !== 'N/A') {
                  const date = parseBrazilianDate(dateToShow);
                  formattedDate = isNaN(date.getTime()) ? 'Invalid Date' : date.toLocaleString('pt-BR');
                }
                
                return (
                  <div style={{
                    background: '#0C5C75',
                    color: 'white',
                    padding: '9px 12px',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    fontSize: '0.8em'
                  }}>
                    <strong>{point.serieId}</strong><br/>
                    Data: {formattedDate}<br/>
                    Valor: {point.data.y}
                  </div>
                );
              }}
              markers={[
                {
                  axis: 'y',
                  value: 0,
                  lineStyle: { stroke: '#0C5C75', strokeWidth: 1 },
                },
              ]}
              colors={['#FFB30D', '#00CFEF', '#0C5C75', '#e3eff4']}
              enableSlices={false}
              areaOpacity={0.2}
              motionStiffness={100}
              enableGridY={false}
              enableGridX
              enablePoints={true}
              legends={ legends }
            />
        </S.GraphContainer>
          );
} 